import { AutoFixHigh } from "@mui/icons-material";
import LyricsOutlinedIcon from "@mui/icons-material/LyricsOutlined";
import { Box, LinearProgress, Tooltip } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { useState } from "react";
import IwButton from "../../components/app/IwButton";
import MaterialModal from "../../components/app/material-modal";
import {
  CUSTOMER_SEGMENTS_CATEGORY_NAME,
  HYPOTHESIS_STATES,
} from "../../components/canvas/constants";
import TemplateCreationForm from "../../components/discovery/template/TemplateCreationForm";
import TemplateEditorModal from "../../components/discovery/template/TemplateEditorModal";
import TemplateList from "../../components/discovery/template/TemplateList";
import GenerateAITemplateModal from "../../components/templates/generate-template-ai-modal";
import { baseAtom } from "../../stores/jotai/base";
import {
  setSelectedTemplate,
  templatesAtom,
} from "../../stores/jotai/template/templates";
import { useGetTemplates } from "../../tanstackQuery/queries/templates";
import EmptySegmentCard from "./EmptySegmentCard";
import HomeSegmentCard from "./HomeSegmentCard";
import HomeSegmentContent from "./HomeSegmentContent";
import HomeSegmentTitle from "./HomeSegmentTitle";
import InterviewTemplateSkeleton from "./Skeleton/InterviewTemplateSkeleton";

interface props {
  isCanvasLoading?: boolean;
  canvas?: any;
}

const getCustomerSegmentEntries = (canvas) => {
  const categorieIDs = Object.keys(canvas?.categories);

  let foundCategory = undefined;

  for (let i = 0; i < categorieIDs?.length; i++) {
    const category = canvas?.categories?.[categorieIDs[i]];

    if (category !== undefined) {
      if (category.name === CUSTOMER_SEGMENTS_CATEGORY_NAME) {
        foundCategory = category;
      }
    }
  }

  if (foundCategory === undefined) return [];

  let cleanedEntries = [];

  for (let i = 0; i < foundCategory?.entries?.length; i++) {
    const isDeleted = foundCategory?.entries[i].state?.name
      ? foundCategory.entries[i].state?.name === HYPOTHESIS_STATES.DELETED
      : foundCategory.entries[i].state === HYPOTHESIS_STATES.DELETED;

    if (foundCategory?.entries[i]?.entry !== "" && !isDeleted) {
      cleanedEntries.push(foundCategory.entries[i]);
    } else {
    }
  }

  return cleanedEntries;
};
const userAtom =  selectAtom(baseAtom, (base) => base.user)
const teamAtom =  selectAtom(baseAtom, (base) => base.team)

const TemplateSegmentCard = ({ isCanvasLoading, canvas }: props) => {
  const [showTemplateEditor, setShowTemplateEditor] = useState<boolean>(false);
  const [base, setBase] = useAtom(baseAtom);
  const setTemplateAtomState = useSetAtom(templatesAtom);
  const [showAiTemplateModal, setShowAiTemplateModal] = useState(false);
  const [showTemplateModal,setShowTemplateModal] = useState(false);
  const [isTemplateCreationModalOpen, setIsTemplateCreationModalOpen] =
          useState<boolean>(false);
  const { keycloak } = useKeycloak();
  const teamID = base.team.groupID;
  const [team] = useAtom(teamAtom);
  const [user] = useAtom(userAtom); 
  const {
    data: templateDetails,
    isLoading: isTemplateLoading,
  } = useGetTemplates({ accessToken: keycloak.token }, { groupID: teamID }, {enabled: !!(base?.team?.groupID)});

  const handleTemplateModalClose = () => {
    setIsTemplateCreationModalOpen(false);
  };
  const isBaseLoading =  Object.keys(base.user || {}).length === 0 ;
  return (
    <>
    {
       (isCanvasLoading || isTemplateLoading|| isBaseLoading ) ? <InterviewTemplateSkeleton/> : <HomeSegmentCard>
       {isCanvasLoading ? (
         <LinearProgress />
       ) : (
         <HomeSegmentTitle>
           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
             <LyricsOutlinedIcon /> Interview Templates
           </Box>
 
           {showTemplateModal && (
             <IwButton
               variant="outlined"
               size="large"
               onClick={() => {
                 setIsTemplateCreationModalOpen(true);
               }}
             >
               create template
             </IwButton>
           )}
           {canvas?.categories?.length &&
            templateDetails?.templates?.length > 0 && base.team?.isAiInterviewTemplateGenerationEnabled && ( 
               <Box sx={{ marginLeft: "auto" ,display: 'inline-block'}}>
                 <IwButton
                   variant="outlined"
                   startIcon={<AutoFixHigh />}
                   size="large"
                   onClick={() => {
                     setShowAiTemplateModal(true);
                   }}
                 >
                   Generate with AI
                 </IwButton>
               </Box>
             )}
         </HomeSegmentTitle>
       )}
 
       <HomeSegmentContent>
         {isTemplateLoading ? (
           <LinearProgress />
         ) : templateDetails?.templates?.length > 0 ? (
           <TemplateList
             templateTableFromHome={true}
             isHome={true}
             onSelectRow={() => {
               setShowTemplateEditor(true);
             }}
             filteredTemplates={templateDetails?.templates || []}
             filteredTemplatesUsers={templateDetails?.users || []}
           />
         ) : (
           <EmptySegmentCard
             segmentName="template"
             generateTemplate={() => {
               setShowAiTemplateModal(true);
             }}
           />
         )}
 
         <TemplateEditorModal
           open={showTemplateEditor}
           hideBackButton={true}
           handleDialogClose={() => {
             setShowTemplateEditor(false);
             setSelectedTemplate(setTemplateAtomState, null);
           }}
           setBase={setBase}
         />
         <MaterialModal
           title="Create Template"
           open={isTemplateCreationModalOpen}
           onClose={handleTemplateModalClose}
           handleDialogClose={handleTemplateModalClose}
           styleOverrides={{
             container: {
               "& .MuiDialog-paper": {
                 width: "100%",
                 minWidth: "812px",
               },
             },
           }}
         >
           <TemplateCreationForm
             showSaveAndOpenButton={true}
             onClose={handleTemplateModalClose}
             onSuccess={handleTemplateModalClose}
             team={team}
             user={user}
             setBase={setBase}
           />
         </MaterialModal>
 
         {isCanvasLoading ? (
           <LinearProgress />
         ) : (
           canvas?.categories && (
             <GenerateAITemplateModal
               open={showAiTemplateModal}
               entries={getCustomerSegmentEntries(canvas)}
               handleDialogClose={() => setShowAiTemplateModal(false)}
               canvas={canvas}
               setBase={setBase}
             />
           )
         )}
       </HomeSegmentContent>
     </HomeSegmentCard>
    }
   
    </>
  );
};

export default TemplateSegmentCard;
